import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetDepositPaymentPerDayQuery } from '../../../../api/queries/getDepositPaymentPerDay';
import { type StatisticFilterPayload, ModuleRole, ApplicationStage } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import { type Datum } from '../shared/typings';
import useSeriesFields from '../shared/useSeriesField';

const usePaymentDepositReceivedData = () => {
    const { currentMonth, lastMonth } = useSeriesFields();
    const [isLoading, setIsLoading] = useState(false);

    // prepare filter
    const company = useCompany(true);
    const { dealerIds } = useMultipleDealerIds();
    const filter: StatisticFilterPayload = useMemo(
        () => ({
            companyId: company?.id,
            dealerIds,
            moduleRole: ModuleRole.Application,
            stage: ApplicationStage.Financing,
        }),
        [company, dealerIds]
    );

    // fetch data
    const { data, loading, error } = useGetDepositPaymentPerDayQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter,
        },
        skip: !company?.id,
    });

    useEffect(() => {
        setIsLoading(loading);
    }, [loading]);

    // transform response data
    const generateData = useCallback(
        (applyData: (data: Datum) => void) => {
            if (!data?.metrics) {
                applyData(null);

                return;
            }

            setIsLoading(true);
            const transformed: Datum = {
                [currentMonth]:
                    data.metrics.currentMonth?.map(({ date, amount }) => ({
                        day: dayjs(date).format('DD'),
                        amount,
                    })) || [],
                [lastMonth]:
                    data.metrics.lastMonth?.map(({ date, amount }) => ({
                        day: dayjs(date).format('DD'),
                        amount,
                    })) || [],
            };
            applyData(transformed);
            setIsLoading(false);
        },
        [data, currentMonth, lastMonth]
    );

    return useMemo(
        () => ({
            isLoading,
            error,
            generateData,
        }),
        [isLoading, error, generateData]
    );
};
export default usePaymentDepositReceivedData;
